import { useState } from 'react';
import { Button } from "@/shared/ui/button.tsx";
import { Dialog, DialogContent, DialogTrigger } from "@/shared/ui/dialog.tsx";
import { Copy, Download, Pencil } from "lucide-react";
import { motion } from 'motion/react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/shared/ui/carousel.tsx";
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

type Props = {
  images: {
    image_id: number;
    image_url: string;
  }[];
  className?: string;
  image: number;
  setImage: (index: number) => void
}

const ImageCarousel = ({ images, className, setImage, image }: Props) => {

  const navigate = useNavigate()

  const [currentIndex, setCurrentIndex] = useState(image);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [previousIndex, setPreviousIndex] = useState(0);

  const handlePrevious = () => {
    if (isTransitioning) return;
    setPreviousIndex(currentIndex);
    setIsTransitioning(true);
    const newIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setImage(newIndex)
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const handleNext = () => {
    if (isTransitioning) return;
    setPreviousIndex(currentIndex);
    setIsTransitioning(true);
    const newIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);

    setImage(newIndex)
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const handleThumbnailClick = (index: number) => {
    if (isTransitioning || index === currentIndex) return;
    setPreviousIndex(currentIndex);
    setIsTransitioning(true);
    setCurrentIndex(index);

    setImage(index)
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const inferredDirection =
    (currentIndex === 0 && previousIndex === images.length - 1)
      ? 'next'
      : (currentIndex === images.length - 1 && previousIndex === 0)
        ? 'prev'
        : currentIndex > previousIndex
          ? 'next'
          : 'prev';

  return (
    <div className={cn("w-full max-w-4xl mx-auto", className)}>
      {/* Main Carousel */}
      <div className="relative mb-3">
        <div className="aspect-square overflow-hidden rounded-lg bg-gray-100 relative">
          {/* Previous image sliding out to left */}
          {isTransitioning && (
            <motion.img
              src={images[previousIndex].image_url}
              alt={`Previous Image ${previousIndex + 1}`}
              className="absolute inset-0 w-full h-full object-cover"
              initial={{ x: 0 }}
              animate={{ x: inferredDirection === 'next' ? '-100%' : '100%' }}
              transition={{
                type: "tween",
                ease: "easeInOut",
                duration: 0.3
              }}
            />
          )}

          {/* Current image sliding in from right */}

          <Dialog>
            <DialogTrigger>
              <motion.img
                key={currentIndex}
                src={images[currentIndex].image_url}
                alt={`Image ${currentIndex + 1}`}
                className="w-full h-full object-cover"
                initial={
                  isTransitioning
                    ? { x: inferredDirection === 'next' ? '100%' : '-100%' }
                    : { x: 0 }
                }
                animate={{ x: 0 }}
                transition={{
                  type: "tween",
                  ease: "easeInOut",
                  duration: 0.3
                }}
              />
            </DialogTrigger>
            <DialogContent className="max-w-2xl bg-black text-purple-500 font-semibold border-none outline-black p-0">
              <div className="absolute bottom-2 right-2 flex items-center gap-2">
                <Button size="icon"
                  title="Download Image"
                  className=""
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = images[currentIndex].image_url;
                    link.download = images[currentIndex].image_url.split('/').pop() || 'image';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}>
                  <Download />
                </Button>
                <Button size="icon"
                  onClick={() => {
                    navigator.clipboard.writeText("")
                  }}
                >
                  <Copy />
                </Button>
                <Button size="icon"
                  onClick={() => {
                    navigate(`/home/gallery/${images[currentIndex].image_id}`)
                  }}
                >
                  <Pencil />
                </Button>
              </div>
              <img src={images[currentIndex].image_url} className="w-full h-auto rounded-lg" alt="Full View" />
            </DialogContent>
          </Dialog>


        </div>

        {/* Custom Navigation Buttons */}
        <button
          onClick={handlePrevious}
          disabled={currentIndex === 0}
          className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white shadow-lg rounded-full p-1 transition-all duration-200 hover:scale-110"
        >
          <ChevronLeft className="size-5 text-gray-800" />
        </button>

        <button
          onClick={handleNext}
          disabled={currentIndex === images.length - 1}
          className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white shadow-lg rounded-full p-2 transition-all duration-200 hover:scale-110"
        >
          <ChevronRight className="size-4 text-gray-800" />
        </button>
      </div>

      {/* Preview Carousel */}
      <div className="relative">
        <Carousel
          opts={{
            align: "start",
            loop: false,
          }}
          className="w-4/5 mx-auto p-1"
        >
          <CarouselContent className="-ml-2 md:-ml-4">
            {images.map((image, index) => (
              <CarouselItem key={index} className="pl-2 basis-1/3">
                <div
                  className={cn(
                    "aspect-square overflow-hidden rounded-lg cursor-pointer transition-all duration-200",
                    currentIndex === index
                      ? "ring-2 ring-blue-500 ring-offset-2 opacity-100"
                      : "opacity-60 hover:opacity-80"
                  )}
                  onClick={() => handleThumbnailClick(index)}
                >
                  <img
                    src={image.image_url}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="" />
          <CarouselNext />
        </Carousel>
      </div>
    </div>
  );
};

export default ImageCarousel;
